import React, { useState } from 'react';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import { UserType } from '../../common/commonTypes';
import { Button, Chip, IconButton, Link } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TelegramIcon from '@mui/icons-material/Telegram';
import XIcon from '@mui/icons-material/X';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import FacebookIcon from '@mui/icons-material/Facebook';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CalculateIcon from '@mui/icons-material/Calculate';
import SettingsIcon from '@mui/icons-material/Settings';
import MessageIcon from '@mui/icons-material/Message';
import LoadingButton from '@mui/lab/LoadingButton';
import SubscribeModal from './SubscribeModal';
import { isTrialExpired, isUserUnsubscribed } from '../../utils/helpers';
import { getUrlToStripeUserPortal } from '../../api';
import { useTranslation } from 'react-i18next';
import SettingsModal from './SettingsModal';
import Snackbar from '@mui/material/Snackbar';
import ContactModal from './ContactModal';
import CalculatorModal from './CalculatorModal';

type FavoritesType = {
    user: UserType;
    close: () => void;
};

const shareData = {
    title: 'CalPal.Pro',
    text: 'I use this new AI Calories Tracker. Try it out!',
    url: 'https://t.me/CalPalHangry_bot',
    email: 'info@calpal.pro',
};

const DrawerContent = (props: FavoritesType) => {
    const { user, close } = props;

    const { t, i18n } = useTranslation();

    const isUserOnTrial: boolean = !user?.settings?.subscribed && !isTrialExpired(user.createdAt);
    const isUserUnSubscribed: boolean = isUserUnsubscribed(user);
    const isUserSubscribed: boolean = user?.settings?.subscribed;

    const [showSubscribeModal, setShowSubscribeModal] = useState(false);
    const [loadingStripeUserPortalUrl, setLoadingStripeUserPortalUrl] = useState(false);

    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [showCalculatorModal, setShowCalculatorModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const [showContactModal, setShowContactModal] = useState(false);

    const goToStripeUserPortal = async () => {
        setLoadingStripeUserPortalUrl(true);
        try {
            const url = await getUrlToStripeUserPortal(
                user.settings.stripeId as string,
                user.settings.languageCode as string
            );
            window.location.href = url;
        } catch (err) {
            setLoadingStripeUserPortalUrl(false);
            alert('Cannot open Stripe user portal. Please try again later.'); // todo: Global error handler
        }
    };

    const shareTelegram = async () => {
        const telegramShareUrl = `https://t.me/share/url?url=${encodeURIComponent(shareData.url)}&text=${encodeURIComponent(shareData.text)}`;
        window.open(telegramShareUrl, '_blank');
    };

    const shareX = async () => {
        const xShareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareData.url)}&text=${encodeURIComponent(shareData.text)}`;
        window.open(xShareUrl, '_blank');
    };

    const shareWatsApp = async () => {
        const wShareUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(shareData.text)}%20${encodeURIComponent(shareData.url)}`;
        window.open(wShareUrl, '_blank');
    };

    const shareFB = async () => {
        const fbShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareData.url)}`;
        window.open(fbShareUrl, '_blank');
    };

    const handleCopy = (link: string) => {
        navigator.clipboard.writeText(link);
    };

    return (
        <div className="w-72 h-full flex flex-col">
            <div className="p-4 flex items-center">
                <Avatar alt={`${user.firstName} ${user.lastName}`} src={user.misc.photo_url} />
                <div className="ml-2 text-sm">{`${user.firstName} ${user.lastName}`}</div>
                <IconButton className="!ml-auto" onClick={close}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </div>

            <Divider flexItem />

            <div className="p-4 flex justify-between">
                {isUserOnTrial && (
                    <>
                        <Chip
                            icon={<MonetizationOnIcon color="success" />}
                            label={t('DRAWER_CHIP_free_trial')}
                            color="default"
                        />

                        <Button
                            onClick={() => setShowSubscribeModal(true)}
                            variant="contained"
                            size="small"
                            sx={{ textTransform: 'none' }}
                        >
                            {t('DRAWER_BTN_subscribe')}
                        </Button>
                    </>
                )}

                {isUserUnSubscribed && (
                    <>
                        <Chip
                            icon={<MonetizationOnIcon color="error" />}
                            label={t('DRAWER_CHIP_trial_expired')}
                            color="default"
                        />

                        <Button
                            onClick={() => setShowSubscribeModal(true)}
                            variant="contained"
                            size="small"
                            sx={{ textTransform: 'none' }}
                        >
                            {t('DRAWER_BTN_subscribe')}
                        </Button>
                    </>
                )}

                {isUserSubscribed && (
                    <>
                        <Chip
                            icon={<MonetizationOnIcon color="success" />}
                            label={t('DRAWER_CHIP_subscribed')}
                            color="default"
                        />

                        <LoadingButton
                            onClick={goToStripeUserPortal}
                            loading={loadingStripeUserPortalUrl}
                            variant="contained"
                            size="small"
                            sx={{ textTransform: 'none' }}
                        >
                            {t('DRAWER_BTN_manage')}
                        </LoadingButton>
                    </>
                )}
            </div>

            <Divider flexItem />

            <div className="p-4 flex flex-col items-center">
                <div className="text-xs text-gray-700 text-center">
                    {t('DRAWER_LABEL_share_with_contacts')}
                </div>
                <div className="flex">
                    <IconButton onClick={shareTelegram}>
                        <TelegramIcon />
                    </IconButton>
                    <IconButton onClick={shareX}>
                        <XIcon />
                    </IconButton>
                    <IconButton onClick={shareWatsApp}>
                        <WhatsAppIcon />
                    </IconButton>
                    <IconButton onClick={shareFB}>
                        <FacebookIcon />
                    </IconButton>
                </div>

                <Divider flexItem className="!my-2 text-sm text-gray-700">
                    {t('DRAWER_LABEL_or')}
                </Divider>

                <div className="text-xs text-gray-700 text-center">
                    {t('DRAWER_LABEL_copy_place_url')}
                </div>

                <Chip
                    label={shareData.url}
                    onClick={() => {
                        handleCopy(shareData.url);
                    }}
                    color="default"
                    clickable
                    deleteIcon={<ContentCopyIcon fontSize="small" color="secondary" />}
                    onDelete={() => {
                        handleCopy(shareData.url);
                    }}
                    sx={{
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                />
            </div>

            <Divider flexItem />

            <div className="p-4 flex flex-col items-center">
                <Button
                    onClick={() => setShowContactModal(true)}
                    variant="contained"
                    size="small"
                    sx={{ textTransform: 'none' }}
                    startIcon={<MessageIcon />}
                >
                    {t('DRAWER_BTN_contact')}
                </Button>

                <Divider flexItem className="!my-2 text-sm text-gray-700">
                    {t('DRAWER_LABEL_or')}
                </Divider>

                <div className="text-xs text-gray-700 text-center">
                    {t('DRAWER_LABEL_questions_feature_requests')}
                </div>
                <Chip
                    label={shareData.email}
                    onClick={() => {
                        handleCopy(shareData.email);
                    }}
                    color="default"
                    clickable
                    deleteIcon={<ContentCopyIcon fontSize="small" color="secondary" />}
                    onDelete={() => {
                        handleCopy(shareData.email);
                    }}
                    sx={{
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                />
            </div>

            <Divider flexItem />

            <div className="p-4 flex items-center justify-between">
                <Button
                    onClick={() => setShowCalculatorModal(true)}
                    variant="contained"
                    size="small"
                    sx={{ textTransform: 'none' }}
                    startIcon={<CalculateIcon />}
                >
                    {t('DRAWER_BTN_calculator')}
                </Button>

                <Button
                    onClick={() => setShowSettingsModal(true)}
                    variant="contained"
                    size="small"
                    sx={{ textTransform: 'none' }}
                    startIcon={<SettingsIcon />}
                >
                    {t('DRAWER_BTN_settings')}
                </Button>
            </div>

            <Divider flexItem />

            <div className="mt-auto mb-4 text-center text-sm">
                <div className="mb-2">
                    <Link
                        target="_blank"
                        rel="noreferrer"
                        href={`https://calpal.pro/${i18n.language === 'en' ? '' : i18n.language}`}
                    >
                        {t('DRAWER_LINK_about')}
                        <OpenInNewIcon sx={{ fontSize: '1rem' }} />
                    </Link>
                </div>
                <div>
                    <Link
                        target="_blank"
                        rel="noreferrer"
                        href={`https://calpal.pro/terms-${i18n.language}.html`}
                    >
                        {t('DRAWER_LINK_toc')}
                        <OpenInNewIcon sx={{ fontSize: '1rem' }} />
                    </Link>
                </div>
            </div>

            {showSubscribeModal && (
                <SubscribeModal user={user} onCancel={() => setShowSubscribeModal(false)} />
            )}

            {showSettingsModal && (
                <SettingsModal
                    user={user}
                    onCancel={() => setShowSettingsModal(false)}
                    onSuccess={() => {
                        setShowSettingsModal(false);
                        setSuccessMessage(t('SUCCESS_MSG_settings_saved'));
                        // close(); // todo: if we close it now, success message will be removed immediately
                    }}
                />
            )}

            {showCalculatorModal && (
                <CalculatorModal
                    user={user}
                    onCancel={() => setShowCalculatorModal(false)}
                    onSuccess={() => {
                        setShowCalculatorModal(false);
                        setSuccessMessage(t('SUCCESS_MSG_settings_saved'));
                        // close(); // todo: if we close it now, success message will be removed immediately
                    }}
                />
            )}

            {showContactModal && (
                <ContactModal
                    user={user}
                    onCancel={() => setShowContactModal(false)}
                    onSuccess={() => {
                        setShowContactModal(false);
                        setSuccessMessage(t('SUCCESS_MSG_message_sent'));
                        // close(); // todo: if we close it now, success message will be removed immediately
                    }}
                />
            )}

            {successMessage && (
                <Snackbar
                    open
                    autoHideDuration={3000}
                    onClose={() => {
                        setSuccessMessage(null);
                    }}
                    message={successMessage}
                />
            )}
        </div>
    );
};

export default DrawerContent;
